<template>
  <mi-drawer
    class="validation-details-drawer"
    :class="opened? 'opened' : 'closed'"
    :model-value="true"
    :mini="!opened"
    width="355"
    mini-width="57"
    side="right"
    overlay
    bordered
    persistent
  >
    <div class="mini-drawer">
      <h4 v-show="opened" class="header">Details</h4>
      <mi-btn
        :ripple="false"
        icon="arrows-right"
        icon-type
        icon-size="18px"
        class="collapse-icon drawer-btn q-mt-sm"
        :class="{ 'rotate-180': !opened }"
        dense
        fab
        flat
        @click="toggleDrawerOpened()"
      ></mi-btn>
    </div>
    <div v-show="opened && validDetails" class="validation-details__info q-pb-md">
      <div class="validation-details__info-section">
        <h5 class="title__small">Validation ID </h5>
        <p>{{ $route.params.id || '' }}</p>
      </div>
      <div class="flex row">
        <div class="validation-details__info-section fixed-width">
          <h5 class="title__small">Product Model</h5>
          <p v-if="bomValidationDetails.productModelType === 'PRODUCT_MODEL'">
            {{ bomValidationDetails.businessName || '-' }}
          </p>
          <p v-if="bomValidationDetails.productModelType === 'USER_EXPORTS'">
            {{ bomValidationDetails.userExportsName || '-' }}
          </p>
        </div>
        <div class="validation-details__info-section">
          <h5 class="title__small">Planning Period</h5>
          <p>
            {{ removePrefixFromPlanningPeriod(bomValidationDetails?.planningPeriodRange?.from) }}
          </p>
        </div>
      </div>
      <div class="flex row">
        <div class="validation-details__info-section fixed-width">
          <h5 class="title__small">Creation Date</h5>
          <p>{{ creationDateFormatted || '' }}</p>
        </div>
        <div class="validation-details__info-section">
          <h5 class="title__small">Component ID</h5>
          <p>{{ bomValidationDetails.componentId || '' }}</p>
        </div>
      </div>
      <div class="flex row">
        <div class="validation-details__info-section fixed-width">
          <h5 class="title__small">Precondition</h5>
          <div v-if="hasPreconditions" class="precondition-row">
            <span class="precondition">{{ formattedPrecondition }}</span>
            <span v-if="resolvedPreconditions.length" class="precondition-info-icon-wrapper">
              <mi-icon
                name="info-circle"
                size="16px"
                class="precondition-info-icon"
              >
              </mi-icon>
              <mi-tooltip self="bottom left" max-width="300px" light>
                <div class="precondition-tooltip-list">
                  <span
                    v-for="item in resolvedPreconditions"
                    :key="item.id2"
                    class="precondition-tooltip-item"
                  >
                    {{ item.id2 }} - {{ item.name }}
                  </span>
                </div>
              </mi-tooltip>
            </span>
          </div>
          <p v-else class="precondition">--</p>
        </div>
      </div>
    </div>
  </mi-drawer>
</template>

<script>
  import { createNamespacedHelpers } from 'vuex'
  import formatDate from '@/utils/formatDate'
  import { getAutocompleteSuggestions, getProductModels } from '@/api'
  import MiBtn from '../../../../packages/@mi-library/MiBtn/MiBtn.vue'
  import MiTooltip from '../../../../packages/@mi-library/MiTooltip/MiTooltip.vue'
  import { removePrefixFromPlanningPeriod } from '../../../../utils/planningPeriod'

  const { mapState } = createNamespacedHelpers('product-model')

  export default {
    name: 'BomValidationResultsDetails',
    components: { MiBtn, MiTooltip },
    props: {
      bomValidationDetails: {
        type: Object,
        required: true
      }
    },
    emits: ['is-drawer-opened'],
    data: () => ({
      opened: true,
      resolvedPreconditions: []
    }),
    computed: {
      ...mapState(['productModels']),
      creationDateFormatted() {
        return this.bomValidationDetails.startDate
          ? formatDate(this.bomValidationDetails.startDate)
          : ''
      },
      formattedPrecondition() {
        return this.bomValidationDetails.precondition?.length > 0
          ? this.bomValidationDetails.precondition.join('; ')
          : '--'
      },
      hasPreconditions() {
        return this.bomValidationDetails.precondition?.length > 0
      },
      validDetails() {
        return Object.keys(this.bomValidationDetails).length > 0
          && this.bomValidationDetails.businessName != null
          && this.bomValidationDetails.componentId != null
          && this.bomValidationDetails.planningPeriodRange != null
          && this.bomValidationDetails.startDate
      },
      productModelInternalName() {
        const { businessName, encodedBusinessName } = this.bomValidationDetails
        const match = this.productModels.find(
          pm => pm.businessName === businessName || pm.encodedBusinessName === encodedBusinessName
        )

        return match?.name || ''
      }
    },
    watch: {
      'bomValidationDetails.precondition': {
        immediate: true,
        handler() {
          this.fetchPreconditionNames()
        }
      },
      productModelInternalName: {
        handler(newVal) {
          if (newVal) {
            this.fetchPreconditionNames()
          }
        }
      }
    },
    methods: {
      removePrefixFromPlanningPeriod,
      toggleDrawerOpened() {
        this.opened = !this.opened
        this.$emit('is-drawer-opened', this.opened)
      },
      async fetchPreconditionNames() {
        const preconditions = this.bomValidationDetails.precondition
        const { businessName, encodedBusinessName } = this.bomValidationDetails

        if (!preconditions?.length || (!businessName && !encodedBusinessName)) {
          return
        }

        try {
          // Resolve internal product model name from API
          let productModelName = this.productModelInternalName

          if (!productModelName) {
            const productModels = await getProductModels()
            const match = productModels.find(
              pm => pm.businessName === businessName
                || pm.encodedBusinessName === encodedBusinessName
            )

            productModelName = match?.name || ''
          }

          if (!productModelName) {
            return
          }

          const results = await Promise.all(
            preconditions.map(async id => {
              const response = await getAutocompleteSuggestions({
                productModelName,
                input: id,
                limit: 1
              })

              const match = response?.suggestions?.find(s => s.id2 === id)
              return match || { id2: id, name: id }
            })
          )

          this.resolvedPreconditions = results
        }
        catch (e) {
          this.resolvedPreconditions = preconditions.map(id => ({ id2: id, name: id }))
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  .mini-drawer {
    width: 100%;
    display: flex;
    justify-content: space-between;
    padding: 16px;
    align-items: center;
  }

  .fixed-width {
    width: 204px;
  }

  // stylelint-disable declaration-no-important
  ::v-deep(.q-drawer) {
    top: 132px !important;
  }

  .header {
    margin: 0;
  }

  h5 {
    margin: 0;
    font-size: 14px;
  }

  .bom-validation-details {
    min-width: 380px;
    min-height: 170px;
    background-color: $mi-silver-50;
  }

  .validation-details {
    line-height: 150%;

    ::v-deep(.q-drawer-container) {
      margin-left: 350px !important;
    }

    &__info-section {
      padding: 16px 16px 0;

      p {
        margin-top: 4px;
        margin-bottom: 0;
      }
    }

    &__info-small-section {
      padding: 8px 0 0;
      align-items: baseline;

      p {
        margin-bottom: 0;
      }
    }

    &__info {
      margin: 16px;
      background-color: #eaedf366;
      font-size: 14px;

      .title {
        font-weight: 700;
        font-size: 14px;
        gap: 10px;
        display: flex;
        flex-direction: row;
        font-family: $mi-typography-font-family;
        align-items: baseline;

        &__small {
          font-size: 12px;
          line-height: 18px;
        }
      }
    }
  }

  .precondition {
    word-break: break-word;
  }

  .precondition-row {
    display: flex;
    align-items: center;
    gap: 5px;
    margin-top: 4px;
  }

  .precondition-info-icon-wrapper {
    position: relative;
    display: inline-flex;
    align-items: center;
    cursor: pointer;
  }

  .precondition-info-icon {
    color: $mi-anthracite-800;
  }

  .precondition-tooltip-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .precondition-tooltip-item {
    font-size: 12px;
    line-height: 18px;
  }
</style>
